import React, { useCallback, useEffect, useMemo } from 'react'
import { Button, Stack, Tooltip, Typography, CircularProgress, useTheme } from '@mui/material'
import useLinks from 'hooks/models/useLinks'
import usePoll from 'hooks/usePoll'

import { SyncOutlined, WarningOutlined } from '@ant-design/icons'
import datetolocalize from 'datetolocalize'

const createStyles = (theme) => ({
  button: {
    cursor: 'pointer',
    color: theme.palette.primary.darker,
    padding: '0px',
    minWidth: 'auto',
    '& .MuiButton-icon': {
      margin: 0,
      fontSize: '1rem',
    },
    '&:disabled': {
      color: theme.palette.primary.darker,
    },
  },
})

export default function LinksStatus() {
  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    linksStatus,
    refresh,
    refetch,
    links: { data: links = [] },
  } = useLinks()

  const onRefresh = useCallback(
    async (stopPolling) => {
      const { data } = await refetch()

      if (data.some((link) => link.status === 'sync')) {
        return
      }
      stopPolling()
    },
    [refetch]
  )

  const isLinkInSync = useMemo(() => {
    const TEN_MINUTES = 10 * 60 * 1000 // 10 minutes in milliseconds
    return links?.some((link) => {
      if (link.status !== 'sync') return false
      const lastUpdate = new Date(link.lastUpdate).getTime()
      const now = new Date().getTime()
      return now - lastUpdate < TEN_MINUTES
    })
  }, [links])

  const { startPolling, isPolling: isRefreshing } = usePoll(onRefresh, 5000)

  const refreshAllLinks = useCallback(async () => {
    if (isRefreshing) return
    await refresh()
    startPolling()
  }, [isRefreshing, refresh, startPolling])

  const isLinkInRefresh = useMemo(() => isLinkInSync || isRefreshing, [isLinkInSync, isRefreshing])

  useEffect(() => {
    if (isLinkInSync && !isRefreshing) {
      startPolling()
    }
  }, [isLinkInSync, isRefreshing, startPolling])

  if (!linksStatus?.time || isLinkInRefresh) return null
  return (
    <Stack alignItems={'center'} direction="row" columnGap={0.75}>
      <Button
        disabled={isLinkInRefresh}
        sx={styles.button}
        variant="text"
        startIcon={
          isLinkInRefresh ? (
            <CircularProgress size="14px" color="primary" />
          ) : (
            <SyncOutlined color="#98E6F0" />
          )
        }
        onClick={refreshAllLinks}
      />
      <Tooltip
        title={`Last time transaction data was synced into Merklebase. \n
Note: Instrument prices update every minute.`}
        placement="top"
      >
        <Typography variant="body1">
          {(() => {
            const dateStr = datetolocalize.dateToLocale({ date: linksStatus.time })
            const timeStr = datetolocalize.timeToLocale({ date: linksStatus.time })
            return (
              <>
                {dateStr} <span style={{ textTransform: 'uppercase' }}>{timeStr}</span>
              </>
            )
          })()}
        </Typography>
      </Tooltip>
      {linksStatus.error && (
        <Tooltip
          title={`There was an error syncing trading data
into Merklebase. \n
Please try refreshing. \n
If the issue persists, contact us for assistance`}
          placement="top"
        >
          <WarningOutlined style={{ color: theme.palette.warning.main }} />
        </Tooltip>
      )}
    </Stack>
  )
}
